import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { TID } from "@/lib/testIds";
import { ArrowRight, LogIn } from "lucide-react";
import { useAuth, startGoogleLogin } from "@/lib/auth";
import { fetchFoundersCap, fetchPublicPartnerProfile } from "@/lib/api";
import FirstListenersWall from "@/components/FirstListenersWall";
import { PartnerTestimonialRibbon } from "@/components/PartnerTestimonialRibbon";
import RecentBlogStrip from "@/components/RecentBlogStrip";
import LeadMagnetCTA from "@/components/LeadMagnetCTA";
import HeroTestimonial from "@/components/HeroTestimonial";
import ListenerStories from "@/components/ListenerStories";
import HomeFAQ from "@/components/HomeFAQ";
import ReviewBadgeStrip from "@/components/ReviewBadgeStrip";
import TrustpilotWidget from "@/components/TrustpilotWidget";
import SiteHeader from "@/components/SiteHeader";
import MobileTryBar from "@/components/MobileTryBar";
import { SpokenEntryShot, ChapterListShot, ChapterMirrorShot } from "@/components/ProductShots";
import { PageMeta, ORG_JSONLD } from "@/lib/seo";
import ROUTE_META from "@/lib/routeMeta.json";

const FEATURES = [
  { n: "01", t: "Speak, don't type", d: "The mic listens. Whisper transcribes. Your voice becomes the page.", Shot: SpokenEntryShot },
  { n: "02", t: "Seven chapters", d: "Inner child, core wounds, forgiveness — one at a time, unhurried.", Shot: ChapterListShot },
  { n: "03", t: "A gentle mirror", d: "A soft AI companion offers one reflection back, never a diagnosis.", Shot: ChapterMirrorShot },
];

function ListenerCounter({ cap }) {
  if (!cap) return null;
  const entries = cap.entries_this_week >= 5 ? ` · ${cap.entries_this_week} entries spoken this week` : "";
  const label = cap.closed
    ? `${cap.current} listeners${entries}`
    : `${cap.current} listener${cap.current === 1 ? "" : "s"}${entries} · ${cap.remaining} founding seat${cap.remaining === 1 ? "" : "s"} left`;
  return (
    <Link
      to="/pricing"
      data-testid="founder-cap-badge"
      aria-label={`${label}. See pricing.`}
      className="inline-flex items-center gap-2 border border-terracotta bg-terracotta/20 hover:bg-terracotta/30 px-4 py-2 font-sans text-[11px] sm:text-xs uppercase tracking-[0.22em] font-semibold text-bone-100 transition-colors"
    >
      <span className="h-2 w-2 rounded-full bg-terracotta animate-pulse motion-reduce:animate-none" aria-hidden="true" />
      <span>{label}</span>
    </Link>
  );
}

function HeroCtas({ user, loading, onReturn }) {
  return (
    <>
    <div className="mt-10 sm:mt-14 flex items-center gap-4 sm:gap-6 flex-wrap">
      {user ? (
        <Button data-testid={TID.landingEnter} onClick={onReturn} className="rounded-none px-6 sm:px-8 py-6 bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans tracking-wide text-sm uppercase transition-colors w-full sm:w-auto min-h-[52px]">
          Return to the workbook <ArrowRight className="ml-3 h-4 w-4" />
        </Button>
      ) : (
        <Link to="/try" data-testid="landing-try-cta" className="inline-flex items-center justify-center px-6 sm:px-8 min-h-[52px] bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans tracking-wide text-sm uppercase transition-colors w-full sm:w-auto">
          Try Chapter One — no account <ArrowRight className="ml-3 h-4 w-4" />
        </Link>
      )}
      <Link to="/pricing" data-testid="landing-pricing-link" className="inline-flex items-center min-h-[44px] px-3 sm:px-1 py-2 sm:py-1 font-sans text-sm uppercase tracking-[0.2em] text-bone-400 hover:text-terracotta transition-colors border-b border-transparent hover:border-terracotta">
        See pricing · three plans
      </Link>
      {user && (
        <Link to="/history" className="inline-flex items-center min-h-[44px] px-3 sm:px-1 py-2 sm:py-1 font-sans text-sm uppercase tracking-[0.2em] text-bone-400 hover:text-bone-200 transition-colors" data-testid={TID.historyLink}>
          Read past entries
        </Link>
      )}
    </div>
    {!user && (
      <p className="mt-5 font-sans text-sm text-bone-500" data-testid="landing-signin-line">
        Already a listener?{" "}
        <button data-testid={TID.landingEnter} onClick={startGoogleLogin} disabled={loading} className="inline-flex items-center min-h-[44px] text-bone-300 hover:text-terracotta underline underline-offset-4 transition-colors">
          <LogIn className="mr-1.5 h-3.5 w-3.5" /> Sign in with Google
        </button>
      </p>
    )}
    </>
  );
}

function Features() {
  return (
    <div className="mt-20 sm:mt-28 space-y-16 sm:space-y-20 max-w-5xl" data-testid="landing-features">
      {FEATURES.map(({ n, t, d, Shot }, i) => (
        <div key={n} className="grid grid-cols-1 md:grid-cols-[1fr_1.3fr] gap-8 md:gap-14 items-center animate-fade-up" style={{ animationDelay: `${0.15 + i * 0.12}s` }}>
          <div className={i % 2 ? "md:order-2" : ""}>
            <div className="font-sans text-xs uppercase tracking-[0.28em] text-terracotta mb-3">{n}</div>
            <div className="font-serif text-2xl text-bone-100 mb-2">{t}</div>
            <div className="font-sans text-sm sm:text-base text-bone-400 leading-relaxed max-w-sm">{d}</div>
          </div>
          <Shot />
        </div>
      ))}
    </div>
  );
}

export default function Landing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [cap, setCap] = useState(null);
  const [partnerProfile, setPartnerProfile] = useState(null);

  useEffect(() => {
    fetchFoundersCap().then(setCap).catch(() => setCap(null));
    // Capture affiliate ref from URL so it survives the Google OAuth round-trip.
    let ref = null;
    try {
      ref = new URLSearchParams(window.location.search).get("ref");
      if (ref) window.sessionStorage.setItem("umbra_ref", ref.trim().toLowerCase());
    } catch (e) {
      // sessionStorage disabled — signup will just create an unattributed user.
    }
    if (ref) {
      fetchPublicPartnerProfile(ref.trim().toLowerCase()).then(setPartnerProfile).catch(() => setPartnerProfile(null));
    }
  }, []);

  return (
    <div className="relative min-h-screen bg-obsidian-900 overflow-hidden">
      <PageMeta
        {...ROUTE_META["/"]}
        path="/"
        jsonLd={[ORG_JSONLD]}
      />
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1744033416342-614bfac570fe?crop=entropy&cs=srgb&fm=jpg&w=1600&q=70')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-obsidian-900/60 via-obsidian-900/85 to-obsidian-900" aria-hidden="true" />
      <SiteHeader />

      <div className="relative z-10 max-w-5xl mx-auto px-6 sm:px-8 md:px-16 pt-12 md:pt-28 pb-16">
        <div className="flex items-start sm:items-center gap-2 sm:gap-4 mb-6 sm:mb-8 flex-col sm:flex-row sm:flex-wrap">
          <p className="font-sans uppercase tracking-[0.24em] sm:tracking-[0.28em] text-xs text-bone-400" data-testid="landing-eyebrow">
            Umbra · for people who'd rather speak their shadow work than write it
          </p>
          <ListenerCounter cap={cap} />
        </div>

        <h1 data-testid={TID.landingHero} className="font-serif text-4xl sm:text-6xl lg:text-7xl leading-[1.1] sm:leading-[1.02] text-bone-100 max-w-3xl">
          A quiet shadow work journal <em className="text-terracotta not-italic">for the parts of you</em>{" "}
          <span className="italic font-light text-bone-300">that were never spoken aloud.</span>
        </h1>

        <p className="mt-8 sm:mt-10 max-w-xl font-sans text-base sm:text-lg text-bone-300 leading-relaxed" data-testid="landing-subhead">
          <strong className="font-medium text-bone-100">No blank page to face. Speak your answers. Listen back.</strong>{" "}
          For anyone doing shadow work who doesn't know where to start — seven guided chapters in the tradition of
          Keila Shaheen and Karla Jean, and the journal does the writing while you do the noticing.
        </p>

        {partnerProfile && partnerProfile.testimonial && (
          <PartnerTestimonialRibbon name={partnerProfile.name} quote={partnerProfile.testimonial.quote} />
        )}

        <HeroCtas user={user} loading={loading} onReturn={() => navigate("/dashboard")} />

        <p className="mt-4 font-sans text-xs uppercase tracking-[0.24em] text-bone-500 max-w-md">
          Private by design · your voice, your words, your account
        </p>

        <HeroTestimonial />
        <Features />
        <ListenerStories />
        <ReviewBadgeStrip />
        <TrustpilotWidget />
        <FirstListenersWall />
        <LeadMagnetCTA source="landing" />
        <HomeFAQ />
        <RecentBlogStrip />
      </div>
      {!user && !loading && <MobileTryBar />}
    </div>
  );
}
